<template>
  <div>
    <!-- Company job search bar -->
    <div class="mb-m md:flex items-center gap-6">
      <div class="mb-s md:mb-0 md:max-w-300 w-full cards-container">
        <standard-search
          :prepopulate-city="prepopulateCity"
          :has-location-error="hasLocationError"
          :search-url="`/companies/${companySlug}?tab=jobs`"
          location-api-call="/api/suggest-city-state"
          :jobs-standard-search="true"
          :cdljn-jobs-standard-search="true"
          city-placeholder="Search your city, state or ZIP"
          app-name="cdljobnow"
          :user-filter-options="{}"
          :show-keyword-input="false"
          :company-jobs-standard-search="true"
        ></standard-search>
      </div>

      <div class="text-sm text-onyx font-normal flex">
        <vs-icon
          class="text-lg"
          icon="icon-briefcase"
          icon-pack="feather"
        ></vs-icon>
        <p class="text-14 ml-8px">
          {{ jobsLength }} job{{ jobsLength != 1 ? 's' : '' }} found
        </p>
      </div>
    </div>

    <div v-if="displayJobs.length === 0" class="mt-8">
      <h2 class="text-16 md:text-21 font-bold leading-1-19 text-onyx mb-4 md:mb-24px">
        Dead end! Unfortunately, there are no matches at this time.
      </h2>
      <p class="text-14 md:text-base text-onyx leading-relaxed w-full">
        Try expanding your search or check out some of our other options.
      </p>
    </div>

    <!-- Company job listing -->
    <div class="mb-4 cursor-pointer" v-for="(job, index) in displayJobs" :key="index">
    <a :target="getJobTargetType(job)" :href="getJobUrl(job)" @click="redirectToJobClick(job)">
      <div class="listing-card border-2 border-solid border-transparent">
        <div class="w-auto xl:w-1023px">
          <div class="flex p-6 rounded-8 w-auto lg:w-1023px">
            <div class="hidden md:flex w-16 h-16">
              <img
                  class="flex object-contain w-full h-full rounded-md border border-solid border-chrome-lightest"
                  :src="job['company']['logo']"
                  :alt="job['company']['name'] + ' Logo'"
                  :title="job['company']['name'] +' Logo'"
              />
            </div>
            <div class="flex flex-col md:ml-8 flex-1">
              <div class="flex md:hidden w-16 h-16 mb-4">
                <img
                    class="flex object-contain w-full h-full rounded-md border border-solid border-chrome-lightest"
                    :src="job['company']['logo']"
                    :alt="job['company']['name'] + ' Logo'"
                    :title="job['company']['name'] +' Logo'"
                />
              </div>

              <div class="w-full flex align-center justify-between md:mt-xs">
                <div class="text-18 text-primary-darker font-medium leading-normal m-0 p-0 hover:underline cursor-pointer ellipse-two-lines line-height-normal">
                  {{ job['title'] }}
                </div>

                <span v-if="job['is_featured']" class="hidden md:block h-26px">
                  <vs-chip color="rgba(124, 195, 102, 0.2)" class="self-start">
                      <span class="m-0 p-0 text-12 text-onyx font-bold w-auto text-center">Hiring Now</span>
                  </vs-chip>
                </span>
              </div>

              <div class="p-0 m-0 mt-4px text-14 text-onyx-lightest font-semibold">
                {{ job['company']['name'] }}
              </div>

              <div class="text-onyx-lightest font-normal mt-14px lg:mt-8px w-full flex items-center">
                <div class="h-22px w-22px flex justify-center items-center">
                  <vs-icon class="text-lg" :class="icon.bold" :icon="icon.type" icon-pack="feather"></vs-icon>
                </div>

                <p class="ml-6px text-14 text-onyx-lightest ">{{ job['category'] }}</p>
              </div>

              <span v-if="job['is_featured']" class="md:hidden mt-16px h-26px">
                <vs-chip color="rgba(124, 195, 102, 0.2)" class="self-start mb-0">
                    <span class="m-0 p-0 text-12 text-onyx font-bold w-auto">Hiring Now</span>
                </vs-chip>
              </span>

              <div class="perks-wrapper w-auto mt-16px flex flex-wrap gap-2">

                <vs-chip v-for="(perk, index) in job['job_perks']" :key="index" :color="perk['color_10']" class="m-0 self-start rounded-6px w-auto md:w-72px" style="min-width: fit-content !important;">
                  <span class="m-0 p-0 text-12 text-onyx-lightest font-medium w-auto">
                    <object
                      :title="perk['desc']"
                      :data="perk.icon_url"
                      width="12"
                      height="12"
                      class="translate-icon-object"
                      :aria-label="perk['desc']"
                    >
                      <span>{{ perk['desc'] }}</span>
                    </object>

                    {{ perk['desc'] }}
                  </span>
                </vs-chip>
                <vs-chip
                  v-if="job['is_hiring_event']"
                  class="m-0 self-start rounded-6px w-auto md:w-72px"
                  style="min-width: fit-content !important"
                >
                  <span
                    class="m-0 p-0 text-12 text-onyx-lightest font-medium w-auto"
                  >
                    Hiring Event
                  </span>
                </vs-chip>

              </div>
            </div>
            <hr v-if="showApplyButton" class="divider hidden md:block md:border-chrome-lightest md:border-solid -my-6 md:ml-8 md:mr-23px" />
            <div v-if="showApplyButton" class="hidden md:block my-auto">
              <a :target="getJobTargetType(job)" :href="getJobUrl(job)" @click.stop="redirectToJobClick(job)">
                <primary-button
                  size="large"
                  class="w-full lg:w-187px bg-primary text-white text-14"
                  :aria-label="`Get Started with` + job['title']"
                  :title="`Get Started with` + job['title']">
                  Get Started
                </primary-button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </a>
    </div>

    <div class="w-full mx-auto mt-8 md:mt-10 lg:w-auto flex justify-center" @click="loadMoreJobs"
         v-if="showViewMoreBtn">
      <primary-button size="large" class="bg-primary text-white text-14" title="Load 25 More Job Options" aria-label="Load 25 More Job Options">
        Load {{ loadCount }} More Jobs
        <span class="ml-2" v-show="loadingMore"><i class="fas fa-circle-notch fa-spin"></i></span>
      </primary-button>
    </div>
  </div>
</template>

<script>
import {slugify} from "../../utilities/slugify";
import axios from "axios";
import { SimpleSVG } from 'vue-simple-svg';
import events from "../../mixins/events";
import StandardSearch from "../../components/StandardSearch.vue";
import PrimaryButton from "../../components/Button.vue";
import { getJobLink, redirectToJob } from "../jobUtil";

export default {
  name: "CompanyJobsCdljnViewMore",

  mixins: [events],

  props: {
    jobs: {
      type: Object,
      required: true,
    },
    showApplyButton: {
      type: Boolean,
      default: false,
    },
    thankyouUrl: {
      type: String,
      default: ''
    },
    companySlug: {
      type: String,
      default: ''
    },
    prepopulateCity: {
      type: String,
      default: ''
    },
    hasLocationError: {
      type: Boolean,
      default: false
    },
    icon: {
      type: Object,
    }
  },

  components: {
    SimpleSVG,
    StandardSearch,
    PrimaryButton,
  },

  data: function () {
    return {
      currentPage: 1,
      jobsData: this.jobs.data,
      perPage: this.jobs.per_page,
      totalJobs: this.jobs.total,
      nextPageUrl: this.jobs.next_page_url,
      recommendedJobs: [],
      loadingMore: false,
      showViewMoreBtn: this.jobs.total > this.jobs.per_page,
      jobsRemaining: (this.jobs.total - this.jobs.per_page),
      jobsLength: this.jobs.total,
    }
  },

  computed: {
    displayJobs: {
      get() {
        return this.jobsData;
      },
      set(newValue) {
        this.jobsData = newValue;
      }
    },
    totalPages() {
      return this.recommendedJobs.length;
    },

    loadCount() {
      return this.jobsRemaining > this.perPage ? this.perPage : this.jobsRemaining;
    }
  },

  methods: {
    getJobTargetType(job) {
      return (job['is_linkout'] && !job['is_schedule'])
        ? '_blank'
        : '_self';
    },
    loadMoreJobs(){
      this.loadingMore = true;
      let queryParams = (location.search || '').toString().replace('?', '');
      let loadNextPageUrl = this.nextPageUrl.indexOf('?') === -1
          ? `${this.nextPageUrl}?${queryParams}`
          : `${this.nextPageUrl}&${queryParams}`;

      loadNextPageUrl += loadNextPageUrl.indexOf('?') === -1 ? '?ignore=' : '&ignore=';
      loadNextPageUrl += _.map(this.displayJobs, 'id').join(',');

      axios.get(loadNextPageUrl)
          .then(({ data }) => {

              let newJobs;
              this.currentPage++;
              if (!Array.isArray(data.data)) {
                  newJobs = Object.values(data.data);
              } else {
                newJobs = data.data;
              }

            this.displayJobs = [...this.displayJobs, ...newJobs];
            this.nextPageUrl = data.next_page_url
            this.showViewMoreBtn = this.currentPage < this.jobs.last_page;
            this.jobsRemaining = this.totalJobs - this.displayJobs.length;
            this.loadingMore = false;
          })
          .catch(err => console.log(err));
    },

    getJobUrl(job) {
      return getJobLink(job);
    },

    redirectToJobClick(job) {
      redirectToJob(job, this.trackJobFullAppClick, this.thankyouUrl);
    },

    slugify(str){
      return slugify(str);
    },
  }
}
</script>

<style scoped>
.ellipse-two-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
